import React from "react";
import dayjs from "dayjs";
import { Box, Stack, Text } from "@chakra-ui/react";

const sectionTitleProps = {
    color: "rgba(255, 255, 255, 0.65)",
    fontSize: "0.7rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    mb: 2,
};

const kvRowProps = {
    display: "flex",
    justifyContent: "space-between",
    gap: 4,
    py: 1.5,
    borderBottom: "1px solid rgba(255, 255, 255, 0.05)",
    _last: { borderBottom: "none" },
};

const kvKeyProps = {
    color: "rgba(255, 255, 255, 0.55)",
    fontSize: "0.75rem",
    fontWeight: 500,
};

const kvValueProps = {
    color: "rgba(255, 255, 255, 0.92)",
    fontSize: "0.8rem",
    fontWeight: 600,
    textAlign: "right",
    wordBreak: "break-word",
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
};

/**
 * Stringifies a value for display in the key/value rows.
 * Handles null/undefined, booleans, numbers, and nested objects.
 * @param {*} value - Any JSON-serializable value
 * @returns {string}
 */
function formatValue(value) {
    if (value === null || value === undefined || value === "") return "—";
    if (typeof value === "boolean") return value ? "Oui" : "Non";
    if (typeof value === "object") return JSON.stringify(value);
    return String(value);
}

/**
 * Renders a single key/value row.
 * @param {Object} props
 * @param {string} props.label - Display label
 * @param {*} props.value - Value to format
 * @returns {React.ReactElement}
 */
function KvRow({ label, value }) {
    return (
        <Box {...kvRowProps}>
            <Text {...kvKeyProps}>{label}</Text>
            <Text {...kvValueProps}>{formatValue(value)}</Text>
        </Box>
    );
}

/**
 * Stripe live state section. Renders all keys as a key/value list.
 * @param {Object} props
 * @param {Object} props.stripeState - Live state object from Stripe
 * @returns {React.ReactElement|null}
 */
function StripeStateSection({ stripeState }) {
    if (!stripeState || typeof stripeState !== "object") return null;
    const entries = Object.entries(stripeState);
    if (entries.length === 0) return null;
    return (
        <Box>
            <Text {...sectionTitleProps}>État Stripe (live)</Text>
            <Box>
                {entries.map(([key, value]) => (
                    <KvRow key={key} label={key} value={value} />
                ))}
            </Box>
        </Box>
    );
}

/**
 * Formats a history entry timestamp for display.
 * @param {string} value - ISO datetime string
 * @returns {string}
 */
function formatHistoryDate(value) {
    if (!value) return "—";
    const parsed = dayjs(value);
    return parsed.isValid() ? parsed.format("DD/MM/YYYY HH:mm") : "—";
}

/**
 * Renders a single history entry row (date + action + reason).
 * @param {Object} props
 * @param {Object} props.entry - History entry object
 * @returns {React.ReactElement}
 */
function HistoryEntry({ entry }) {
    return (
        <Box pl={3} borderLeft="2px solid rgba(23, 220, 184, 0.4)" py={1.5} mb={2}>
            <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Text color="rgba(255, 255, 255, 0.95)" fontSize="0.8rem" fontWeight={600}>
                    {entry?.action || entry?.event || "—"}
                </Text>
                <Text color="rgba(255, 255, 255, 0.5)" fontSize="0.7rem">
                    {formatHistoryDate(entry?.created_at || entry?.timestamp || entry?.date)}
                </Text>
            </Stack>
            {entry?.reason && (
                <Text color="rgba(255, 255, 255, 0.65)" fontSize="0.75rem" mt={1}>
                    {entry.reason}
                </Text>
            )}
        </Box>
    );
}

/**
 * History timeline section. Empty state if no entries.
 * @param {Object} props
 * @param {Array} props.history - History entries from the API
 * @returns {React.ReactElement}
 */
function HistorySection({ history }) {
    return (
        <Box>
            <Text {...sectionTitleProps}>Historique</Text>
            {(!history || history.length === 0) ? (
                <Text color="rgba(255, 255, 255, 0.5)" fontSize="0.8rem">
                    Aucun événement
                </Text>
            ) : (
                <Box>{history.map((entry, idx) => <HistoryEntry key={entry?.id || idx} entry={entry} />)}</Box>
            )}
        </Box>
    );
}

export { StripeStateSection, HistorySection };
